import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface I18nState {
  isReady: boolean;
  hasError: boolean;
  error: Error | null;
  useFallback: boolean;
}

const I18N_INITIALIZATION_TIMEOUT = 10000; // 10 seconds
const FALLBACK_LANGUAGE = 'es';

export function useI18nWithFallback() {
  const { i18n } = useTranslation();
  const [state, setState] = useState<I18nState>({
    isReady: false,
    hasError: false,
    error: null,
    useFallback: false
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let mounted = true;

    const handleInitialization = () => {
      if (!mounted) return;

      if (i18n.isInitialized) {
        setState(prev => ({
          ...prev,
          isReady: true,
          hasError: false,
          error: null
        }));
        if (timeoutId) clearTimeout(timeoutId);
        return;
      }

      // Set up timeout for i18n initialization
      timeoutId = setTimeout(() => {
        if (!mounted) return;
        
        console.warn('i18n initialization timeout reached, falling back to default routing');
        setState(prev => ({
          ...prev,
          isReady: true,
          hasError: true,
          error: new Error('i18n initialization timeout'),
          useFallback: true
        }));
        
        // Force fallback language
        try {
          i18n.changeLanguage(FALLBACK_LANGUAGE);
        } catch (err) {
          console.error('Failed to set fallback language:', err);
        }
      }, I18N_INITIALIZATION_TIMEOUT);

      // Listen for initialization events
      const handleInitialized = () => {
        if (!mounted) return;
        setState(prev => ({
          ...prev,
          isReady: true,
          hasError: false,
          error: null
        }));
        if (timeoutId) clearTimeout(timeoutId);
      };

      const handleFailedLoading = (lng: string, ns: string, msg: string) => {
        if (!mounted) return;
        console.error('i18n failed to load resources:', { lng, ns, msg });
        
        // Continue with fallback if this is not the fallback language
        if (lng !== FALLBACK_LANGUAGE) {
          console.warn('Failed to load translations for', lng, ', continuing with fallback');
          return;
        }
        
        // If fallback language also fails, use emergency mode
        setState(prev => ({
          ...prev,
          isReady: true,
          hasError: true,
          error: new Error(`Failed to load translations: ${msg}`),
          useFallback: true
        }));
      };

      // Check if already initialized
      if (i18n.isInitialized) {
        handleInitialized();
      } else {
        // Add event listeners
        i18n.on('initialized', handleInitialized);
        i18n.on('failedLoading', handleFailedLoading);
      }

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        i18n.off('initialized', handleInitialized);
        i18n.off('failedLoading', handleFailedLoading);
      };
    };

    const cleanup = handleInitialization();

    return () => {
      mounted = false;
      if (cleanup) cleanup();
    };
  }, [i18n]);

  return state;
}